import { optionFor, questionById } from "../config/questions";
import { prohibitedSnapshotPhrases, snapshotLanguage } from "../config/snapshot-language";
import type { AssessmentAnswers, FrictionSnapshot, QuestionId } from "../types/friction-finder";
import { normalizePlainText, validateAllAnswers } from "./validation";

export function formatNaturalList(items: readonly string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
}

export function formatAustralianDate(input: string | Date): string {
  return new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "long", year: "numeric", timeZone: "Australia/Adelaide" }).format(new Date(input));
}

function phrases(questionId: QuestionId, ids: string[], customText?: string): string[] {
  return ids.map((id) => {
    if (id === "other_outcome") return `the following outcome: “${normalizePlainText(customText ?? "", 120)}”`;
    const option = optionFor(questionId, id);
    if (!option?.snapshotPhrase) throw new Error(`No Snapshot phrase is configured for ${questionId}.`);
    return option.snapshotPhrase;
  });
}

export function snapshotToPlainText(snapshot: Omit<FrictionSnapshot, "plainTextVersion">): string {
  return [
    "YOUR FRICTION SNAPSHOT",
    "",
    snapshot.intent.toUpperCase(),
    "",
    snapshot.summary,
    "",
    snapshot.impactHeading.toUpperCase(),
    snapshot.impactStatement,
    "",
    "WHAT BETTER LOOKS LIKE",
    snapshot.desiredStateStatement,
    "",
    "WHAT HAPPENS NEXT",
    snapshot.nextStepStatement,
    "",
    `Generated: ${formatAustralianDate(snapshot.generatedAt)}`,
  ].join("\n");
}

export function generateSnapshot(answers: AssessmentAnswers, generatedAt = new Date().toISOString()): FrictionSnapshot {
  const errors = validateAllAnswers(answers);
  if (errors) throw new Error(`Invalid assessment answers: ${Object.values(errors).join(" ")}`);

  const primaryId = answers.primary_friction[0];
  const area = phrases("friction_area", answers.friction_area)[0];
  const affected = formatNaturalList(phrases("affected_groups", answers.affected_groups));
  const summary = primaryId === "other_friction"
    ? `You identified the following main friction: “${normalizePlainText(answers.customFriction ?? "", 160)}” It is most noticeable in ${area}, where it is affecting ${affected}.`
    : `You identified that ${phrases("primary_friction", answers.primary_friction)[0]}. It is most noticeable in ${area}, where it is affecting ${affected}.`;

  const language = answers.timing_intent[0] === "fixing_today" ? snapshotLanguage.fixing : snapshotLanguage.planning;
  const impactStatement = `${language.impactPrefix} ${formatNaturalList(phrases("friction_impacts", answers.friction_impacts))}.`;
  const customOutcomeEndsSentence = answers.desired_outcomes.at(-1) === "other_outcome" && /[.!?]$/u.test(normalizePlainText(answers.customOutcome ?? "", 120));
  const desiredStateStatement = `Better would mean ${formatNaturalList(phrases("desired_outcomes", answers.desired_outcomes, answers.customOutcome))}${customOutcomeEndsSentence ? "" : "."}`;

  const withoutPlainText = {
    title: snapshotLanguage.title,
    intent: language.intent,
    summary,
    impactHeading: language.impactHeading,
    impactStatement,
    desiredStateHeading: snapshotLanguage.desiredStateHeading,
    desiredStateStatement,
    nextStepHeading: snapshotLanguage.nextStepHeading,
    nextStepStatement: language.nextStep,
    generatedAt: new Date(generatedAt).toISOString(),
  } satisfies Omit<FrictionSnapshot, "plainTextVersion">;

  const snapshot: FrictionSnapshot = { ...withoutPlainText, plainTextVersion: snapshotToPlainText(withoutPlainText) };
  const completeText = JSON.stringify(snapshot);
  for (const phrase of prohibitedSnapshotPhrases) {
    if (completeText.toLocaleLowerCase("en-AU").includes(phrase.toLocaleLowerCase("en-AU"))) throw new Error("Snapshot contains prohibited language.");
  }
  if (/\b(undefined|null)\b/.test(completeText)) throw new Error("Snapshot contains an invalid empty value.");
  if (/  +/.test([summary, impactStatement, desiredStateStatement].join(" "))) throw new Error("Snapshot contains doubled spaces.");
  return snapshot;
}

export function getAnswerLabels(questionId: QuestionId, answers: AssessmentAnswers): string[] {
  return answers[questionId].map((id) => {
    if (id === "other_friction") return normalizePlainText(answers.customFriction ?? "", 160);
    if (id === "other_outcome") return normalizePlainText(answers.customOutcome ?? "", 120);
    return optionFor(questionId, id)?.label ?? id;
  });
}

export function allPermittedAnswerIds() {
  return Object.fromEntries(Object.entries(questionById).map(([id, question]) => [id, question.options.map((option) => option.id)]));
}
